import React from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Card } from '@repo/ui'

import blackLarge from 'assets/brand/app-icon/black-620.png'
import blackSmall from 'assets/brand/app-icon/black-560.png'
import purpleTile from 'assets/brand/app-icon/purple-620.png'
import markOnly from 'assets/brand/app-icon/mark.png'

import bareLight128 from 'assets/brand/favicon/bare-light-128.png'
import bareLight32 from 'assets/brand/favicon/bare-light-32.png'
import bareLight16 from 'assets/brand/favicon/bare-light-16.png'
import bareDark128 from 'assets/brand/favicon/bare-dark-128.png'
import bareDark32 from 'assets/brand/favicon/bare-dark-32.png'
import bareDark16 from 'assets/brand/favicon/bare-dark-16.png'
import tileLight128 from 'assets/brand/favicon/tile-light-128.png'
import tileLight32 from 'assets/brand/favicon/tile-light-32.png'
import tileLight16 from 'assets/brand/favicon/tile-light-16.png'
import tileDark128 from 'assets/brand/favicon/tile-dark-128.png'
import tileDark32 from 'assets/brand/favicon/tile-dark-32.png'
import tileDark16 from 'assets/brand/favicon/tile-dark-16.png'

/**
 * iOS masks every app icon to a squircle whose corner radius is ~22.37% of the
 * tile. Previews here apply the same ratio so what you see is what lands on a
 * home screen — an unmasked square reads noticeably different.
 */
const SQUIRCLE = '22.37%'

/** Real iOS home-screen render size. Judging an icon at any other size is misleading. */
const HOME_SCREEN_PT = 60

/** The actual browser tab-strip colours, so "does it disappear" is answerable by eye. */
const CHROME = {
  light: { chrome: '#F1F3F4', ink: '#5F6368' },
  dark: { chrome: '#202124', ink: '#9AA0A6' },
}

const FAVICON_VARIANTS = [
  {
    key: 'tile',
    label: 'Inverting tile, sheened mark',
    shipped: true,
    note: 'White tile with a dark V in light mode, dark tile with a light V in dark mode. The mark is readable because it sits on a field this asset controls, rather than on whatever colour the chrome happens to be — so it survives a custom browser theme or a light browser under a dark OS, where a bare mark vanishes. The sheen echoes the foil on the physical tag; it cannot resolve at 16px and exists for the 32px+ contexts. Worst sheen stop measures 7.29:1 against its own tile, well clear of the 3:1 floor.',
    modes: [
      { mode: 'light', ...CHROME.light, px128: tileLight128, px32: tileLight32, px16: tileLight16 },
      { mode: 'dark', ...CHROME.dark, px128: tileDark128, px32: tileDark32, px16: tileDark16 },
    ],
  },
  {
    key: 'bare',
    label: 'Bare mark, colour flips by theme',
    note: 'Rejected. Maximum mark size — the V spans ~99% of the canvas versus 72% inside the tile — but it assumes the OS theme predicts the browser chrome. When that assumption breaks the mark lands on a field it has no contrast against and disappears entirely. Traded that failure mode for a slightly thinner stroke.',
    modes: [
      { mode: 'light', ...CHROME.light, px128: bareLight128, px32: bareLight32, px16: bareLight16 },
      { mode: 'dark', ...CHROME.dark, px128: bareDark128, px32: bareDark32, px16: bareDark16 },
    ],
  },
]

interface SpecimenProps {
  src: string
  label: string
  note: string
  /** Renders the "shipped" accent + badge. Exactly one specimen should set this. */
  shipped?: boolean
  /** Checkerboard behind the image, for assets with real transparency. */
  transparent?: boolean
}

const Specimen = ({ src, label, note, shipped, transparent }: SpecimenProps) => {
  const theme = useTheme()
  const checker = `linear-gradient(45deg, ${theme.palette.action.hover} 25%, transparent 25%),
     linear-gradient(-45deg, ${theme.palette.action.hover} 25%, transparent 25%),
     linear-gradient(45deg, transparent 75%, ${theme.palette.action.hover} 75%),
     linear-gradient(-45deg, transparent 75%, ${theme.palette.action.hover} 75%)`

  return (
    <Stack spacing={3} sx={{ width: 200 }}>
      <Box
        sx={{
          width: 200,
          height: 200,
          borderRadius: SQUIRCLE,
          overflow: 'hidden',
          ...(transparent && {
            backgroundImage: checker,
            backgroundSize: '16px 16px',
            backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
          }),
          ...(shipped && { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 4 }),
        }}
      >
        <Box component="img" src={src} alt={label} sx={{ width: '100%', height: '100%', display: 'block' }} />
      </Box>

      <Stack spacing={1}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body3" fontWeight={500}>
            {label}
          </Typography>
          {shipped && (
            <Box
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: 10,
                lineHeight: 1.6,
                letterSpacing: '0.04em',
              }}
            >
              SHIPPED
            </Box>
          )}
        </Stack>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {note}
        </Typography>
      </Stack>

      {/* The decisive view: how it actually reads on a home screen. */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          component="img"
          src={src}
          alt={`${label} at home-screen size`}
          sx={{
            width: HOME_SCREEN_PT,
            height: HOME_SCREEN_PT,
            borderRadius: SQUIRCLE,
            display: 'block',
            flexShrink: 0,
          }}
        />
        <Typography variant="body3" color="text.secondary">
          {HOME_SCREEN_PT}&times;{HOME_SCREEN_PT}pt
        </Typography>
      </Stack>
    </Stack>
  )
}

/**
 * /internal/labs/brand — the brand-asset gallery.
 *
 * Exists so design decisions stay reviewable after the conversation that produced
 * them is gone: the shipped icon sits next to the variants that were considered and
 * rejected, each with the reason. Assets are generated, never hand-edited — see the
 * provenance note at the foot of the page.
 */
export const BrandLab = () => (
  <Stack spacing={6} sx={{ pb: 6 }}>
    <Stack spacing={2}>
      <Typography variant="h5">Brand assets</Typography>
      <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 640 }}>
        The Vera App icon and the variants considered for it. Every tile is previewed with
        the iOS squircle mask and at true home-screen size, because an icon judged at poster size
        tells you almost nothing about how it reads on a phone.
      </Typography>
    </Stack>

    <Card>
      <Stack spacing={4}>
        <Typography variant="h6">App icon &mdash; Vera App</Typography>
        <Stack direction="row" spacing={8} flexWrap="wrap" useFlexGap>
          <Specimen
            src={blackLarge}
            label="Matte black, V at 61%"
            note="Mirrors the physical tag: matte body, NW edge highlight, brand-purple bloom at SE, foil V that picks up purple on its lower right."
            shipped
          />
          <Specimen
            src={blackSmall}
            label="Matte black, V at 55%"
            note="Same treatment, smaller mark. More breathing room at the corners; leaves more margin for the iOS 18 tinted and dark icon variants."
          />
          <Specimen
            src={purpleTile}
            label="Brand purple, white V"
            note="Rejected. Higher contrast and more distinctive on a crowded home screen, but reads as a generic SaaS tile rather than the tag, and collides with the platform's purple chrome."
          />
        </Stack>
      </Stack>
    </Card>

    <Card>
      <Stack spacing={4}>
        <Typography variant="h6">Mark</Typography>
        <Stack direction="row" spacing={8} flexWrap="wrap" useFlexGap>
          <Specimen
            src={markOnly}
            label="Foil V, transparent"
            note="The mark alone, no tile. Used for the Android adaptive foreground and the splash screen, where the background is supplied by the OS or the splash config."
            transparent
          />
        </Stack>
      </Stack>
    </Card>

    <Card>
      <Stack spacing={4}>
        <Stack spacing={2}>
          <Typography variant="h6">Favicon</Typography>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 700 }}>
            Shown on the browser chrome each theme actually uses &mdash; Chrome&apos;s light tab strip
            is #F1F3F4, its dark strip #202124. The 16 and 32px tiles are rendered at their true
            raster size, not scaled down from a large PNG, because downscaling in the browser hides
            the stroke mush this comparison exists to reveal.
          </Typography>
        </Stack>

        {FAVICON_VARIANTS.map(v => (
          <Stack key={v.key} spacing={3}>
            <Stack spacing={1}>
              <Typography variant="body3" fontWeight={500}>
                {v.label}
                {v.shipped && (
                  <Box component="span" sx={{ ml: 2, color: 'primary.main', fontSize: 11 }}>
                    &mdash; shipped
                  </Box>
                )}
              </Typography>
              <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 700 }}>
                {v.note}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
              {v.modes.map(m => (
                <Stack
                  key={m.mode}
                  direction="row"
                  spacing={4}
                  alignItems="center"
                  sx={{ px: 4, py: 3, borderRadius: 2, bgcolor: m.chrome, minWidth: 250 }}
                >
                  {[
                    { src: m.px128, size: 64, cap: '64' },
                    { src: m.px32, size: 32, cap: '32' },
                    { src: m.px16, size: 16, cap: '16' },
                  ].map(x => (
                    <Stack key={x.cap} spacing={1} alignItems="center">
                      <Box
                        component="img"
                        src={x.src}
                        alt={`${v.label} ${m.mode} ${x.cap}px`}
                        sx={{ width: x.size, height: x.size, display: 'block', imageRendering: 'auto' }}
                      />
                      <Typography variant="body3" sx={{ fontSize: 10, color: m.ink }}>
                        {x.cap}px
                      </Typography>
                    </Stack>
                  ))}
                  <Typography variant="body3" sx={{ fontSize: 11, color: m.ink, ml: 'auto' }}>
                    {m.mode}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Card>

    <Card>
      <Stack spacing={3}>
        <Typography variant="h6">Provenance</Typography>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 640 }}>
          These PNGs are generated, not hand-edited &mdash; do not retouch them, or the next
          regeneration silently discards the change. The geometry comes from{' '}
          <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            packages/ui/assets/vlogo.svg
          </Box>
          , the single source for the V across web and native.
        </Typography>
        <Box
          component="pre"
          sx={{
            m: 0,
            p: 3,
            borderRadius: 2,
            bgcolor: 'action.hover',
            fontFamily: 'monospace',
            fontSize: 12,
            overflowX: 'auto',
          }}
        >
          node apps/vera-app/scripts/gen-icons.mjs
        </Box>
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5, maxWidth: 640 }}>
          That script writes the shipped 1024px app assets into{' '}
          <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            apps/vera-app/assets/images
          </Box>{' '}
          and the 512px display copies shown here. It requires{' '}
          <Box component="code" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            inkscape
          </Box>{' '}
          on PATH.
        </Typography>
      </Stack>
    </Card>
  </Stack>
)
